import {
  stateAreaPages,
  type StateProjectCaseStudyContent,
  type StateProjectImageKey,
} from "./stateAreaPages";
import { serviceStates } from "./serviceAreas";

export type ProjectMaterialFilter =
  | "Powder Coated Steel"
  | "Solid Plastic"
  | "Stainless Steel"
  | "Plastic Laminate";

export type ProjectFacilityType =
  | "Education"
  | "Hospitality & dining"
  | "Community & public"
  | "Commercial & workplace"
  | "Recreation & resort"
  | "Healthcare"
  | "Faith & community"
  | "Funeral service"
  | "Production & studio";

type ProjectClassification = {
  facilityType: ProjectFacilityType;
  materialFilters: readonly ProjectMaterialFilter[];
};

const classifications = {
  ducksBallpark: { facilityType: "Recreation & resort", materialFilters: [] },
  pierSixtyStalls: { facilityType: "Hospitality & dining", materialFilters: ["Stainless Steel"] },
  johnsTimesSquare: { facilityType: "Hospitality & dining", materialFilters: [] },
  sepaMujerPatchogue: { facilityType: "Community & public", materialFilters: ["Plastic Laminate"] },
  saffronMiddletown: { facilityType: "Hospitality & dining", materialFilters: [] },
  hewlettWoodmerePrimary: { facilityType: "Education", materialFilters: ["Solid Plastic"] },
  buchananElementaryPrimary: { facilityType: "Education", materialFilters: ["Solid Plastic"] },
  cineMagicLicPrimary: { facilityType: "Production & studio", materialFilters: ["Powder Coated Steel"] },
  newPaltzHighSchoolPrimary: { facilityType: "Education", materialFilters: ["Solid Plastic"] },
  itaKitchenGardenCityPrimary: { facilityType: "Hospitality & dining", materialFilters: ["Powder Coated Steel"] },
  metropolitanWarehousePerthAmboyPrimary: { facilityType: "Commercial & workplace", materialFilters: [] },
  onnuriFortLee: { facilityType: "Faith & community", materialFilters: ["Solid Plastic"] },
  robertSpearingParkRidge: { facilityType: "Funeral service", materialFilters: ["Stainless Steel"] },
  youthbuildNewarkSolidPlasticPrimary: {
    facilityType: "Community & public",
    materialFilters: ["Powder Coated Steel", "Solid Plastic"],
  },
  proCraftersTelford: { facilityType: "Commercial & workplace", materialFilters: [] },
  bethelDeliveranceWyncote: { facilityType: "Faith & community", materialFilters: [] },
  veteransMemorialEastHartfordPrimary: { facilityType: "Community & public", materialFilters: ["Powder Coated Steel"] },
  ptsmcWallingfordPrimary: { facilityType: "Healthcare", materialFilters: ["Powder Coated Steel"] },
  hollowParkWoodbury: { facilityType: "Community & public", materialFilters: [] },
  kloecknerMiddletownPrimary: { facilityType: "Commercial & workplace", materialFilters: ["Solid Plastic"] },
  laMaisonGreenwichPrimary: { facilityType: "Hospitality & dining", materialFilters: ["Plastic Laminate"] },
  trainingStationPortWashingtonPrimary: { facilityType: "Recreation & resort", materialFilters: ["Powder Coated Steel"] },
  casaMezcalBurgundyPrimary: { facilityType: "Hospitality & dining", materialFilters: ["Powder Coated Steel"] },
  superParkRvPrimary: { facilityType: "Recreation & resort", materialFilters: ["Powder Coated Steel"] },
  compalGeorgetownStainlessPrimary: { facilityType: "Commercial & workplace", materialFilters: ["Stainless Steel"] },
  outdoorResortIndio: { facilityType: "Recreation & resort", materialFilters: ["Plastic Laminate"] },
} as const satisfies Record<string, ProjectClassification>;

function getClassification(imageKey: StateProjectImageKey): ProjectClassification {
  const classification = classifications[imageKey as keyof typeof classifications];

  if (!classification) {
    throw new Error(`Missing project gallery classification for ${imageKey}`);
  }

  return classification;
}

export type ProjectGalleryItem = {
  project: StateProjectCaseStudyContent;
  stateName: string;
  stateSlug: string;
  facilityType: ProjectFacilityType;
  materialFilters: readonly ProjectMaterialFilter[];
  projectPath: string;
  recordKind: "state-guide" | "gallery-only";
};

const stateGuideProjectItems: readonly ProjectGalleryItem[] = stateAreaPages.flatMap((state) =>
  (state.featuredProjects ?? []).map((project, projectIndex) => {
    const classification = getClassification(project.imageKey);
    const stateDetails = serviceStates.find((serviceState) => serviceState.slug === state.slug);

    if (!stateDetails) {
      throw new Error(`Missing service-area data for ${state.slug}`);
    }

    return {
      project,
      stateName: stateDetails.name,
      stateSlug: state.slug,
      facilityType: classification.facilityType,
      materialFilters: classification.materialFilters,
      projectPath: `/service-areas/${state.slug}#state-project-title-${projectIndex}`,
      recordKind: "state-guide",
    };
  }),
);

const nationwideShippingProjectItems = [{
  project: {
    imageKey: "spiritLifeMinistriesWilmingtonPrimary",
    imageWidth: 1098,
    imageHeight: 914,
    primaryCaption: "Owner-provided project photograph · Spirit Life Ministries International",
    kicker: "Owner-documented project · Wilmington",
    title: "Salmon baked-enamel restroom partitions at Spirit Life Ministries International.",
    location: "3401 Governor Printz Boulevard, Wilmington, Delaware",
    summary: "At Spirit Life Ministries International in Wilmington, an owner-provided project photograph documents an owner-confirmed salmon baked-enamel restroom partition finish direction. This owner-documented faith-and-community project is a finish reference only; its photo does not establish the full quantity, panel material, or final project specification.",
    imageAlt: "Salmon baked-enamel commercial restroom partitions at Spirit Life Ministries International in Wilmington, Delaware",
    venueName: "Spirit Life Ministries International official website",
    venueUrl: "https://spiritlifeministriesinternational.org/",
    venueLinkLabel: "Official Spirit Life Ministries International website",
    metrics: [
      { value: "Salmon", label: "Owner-confirmed baked-enamel finish direction" },
      { value: "Wilmington", label: "Delaware project location" },
    ],
    facts: [
      { label: "Owner-confirmed finish", value: "Salmon baked enamel" },
      { label: "Owner-confirmed setting", value: "Faith and community" },
    ],
    disclosure: "The Wilmington location, salmon baked-enamel finish direction, and photograph were confirmed by Partition Stalls. Exact Partition Stalls role, full scope, panel material, manufacturer, and final specifications are not stated. Delaware is not represented as a full-service installation state.",
  } satisfies StateProjectCaseStudyContent,
  stateName: "Delaware",
  stateSlug: "delaware",
  facilityType: "Faith & community",
  materialFilters: [],
  projectPath: "/projects#project-spirit-life-ministries-wilmington",
  recordKind: "gallery-only",
  }, {
    project: {
      imageKey: "highBanksSpeedwayPuebloPrimary",
      imageWidth: 2048,
      imageHeight: 1536,
      primaryCaption: "Owner-provided project photograph · High Banks Speedway",
      kicker: "Owner-documented project · Pueblo",
      title: "Seven stalls and one privacy screen at High Banks Speedway.",
      location: "Pueblo, Colorado",
      summary: "At High Banks Speedway in Pueblo, two owner-provided project photographs document an owner-confirmed material-supply-only scope of seven restroom stalls and one privacy screen. The photographs show a red partition finish direction; the record does not identify the panel material, manufacturer, mounting configuration, or installation scope.",
      imageAlt: "Red commercial restroom stalls and a privacy screen at High Banks Speedway in Pueblo, Colorado",
      venueName: "High Banks Speedway official website",
      venueUrl: "https://www.highbanksspeedway.com/",
      venueLinkLabel: "Official High Banks Speedway website",
      metrics: [
        { value: "7", label: "Restroom stalls" },
        { value: "1", label: "Privacy screen" },
      ],
      facts: [
        { label: "Partition Stalls role", value: "Material supply only" },
        { label: "Owner-confirmed project scope", value: "Seven stalls + one privacy screen" },
      ],
      gallery: {
        kicker: "Project photographs",
        title: "Two views of the Pueblo material-supply project.",
        copy: "A second owner-provided photograph documents the red restroom partitions and privacy-screen layout supplied for the owner-confirmed seven-stall, one-screen scope.",
        linkLabel: "Official High Banks Speedway website",
        images: [{
          imageKey: "highBanksSpeedwayPuebloScreen",
          width: 2048,
          height: 2048,
          alt: "Red commercial restroom partitions and privacy screen at High Banks Speedway in Pueblo, Colorado",
          caption: "Red restroom partitions and privacy-screen layout",
        }],
      },
      disclosure: "The High Banks Speedway project name, Pueblo location, material-supply-only role, seven-stall quantity, one-privacy-screen quantity, and both restroom photographs were provided by Partition Stalls. The venue’s official website identifies High Banks Speedway as a Pueblo, Colorado track. Exact panel material, manufacturer, finish specification, mounting configuration, installation scope, project date, schedule, performance outcome, accessibility scope, and customer endorsement are not stated. Colorado is not represented as a full-service Partition Stalls installation state.",
    } satisfies StateProjectCaseStudyContent,
    stateName: "Colorado",
    stateSlug: "colorado",
    facilityType: "Recreation & resort",
    materialFilters: [],
    projectPath: "/projects#project-high-banks-speedway-pueblo",
    recordKind: "gallery-only",
  }] as const satisfies readonly ProjectGalleryItem[];

export const projectGalleryItems: readonly ProjectGalleryItem[] = [
  ...stateGuideProjectItems,
  ...nationwideShippingProjectItems,
];

export const projectGalleryStates = Array.from(new Set(projectGalleryItems.map((item) => item.stateName)));
export const projectGalleryMaterials = [
  "Powder Coated Steel",
  "Solid Plastic",
  "Stainless Steel",
  "Plastic Laminate",
] as const satisfies readonly ProjectMaterialFilter[];
export const projectGalleryFacilityTypes = Array.from(new Set(projectGalleryItems.map((item) => item.facilityType)));
